import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./Prototyping.css";

export default function Prototyping() {
    const services = [
        {
            image: "assets/img/GameDevelopment/Creative-UI-Character-Design.png",
            title: "Clickable Game UI Prototypes",
            description: "Turn core screens into clickable prototypes for menus, HUDs, onboarding, stores, rewards, lobbies, and player journeys.",
            points: ["Clickable flows", "Screen states", "UX validation"]
        },
        {
            image: "assets/img/GameDevelopment/3d-game-sprite1.png",
            title: "Gameplay Flow Prototypes",
            description: "Prototype gameplay loops, action feedback, control states, mission flows, score updates, and moment-to-moment UI behavior.",
            points: ["Gameplay loop", "Action feedback", "HUD behavior"]
        },
        {
            image: "assets/img/GameDevelopment/multiplayer_game.jpg",
            title: "Multiplayer UX Prototypes",
            description: "Validate matchmaking, room creation, private tables, ready states, turn flow, player status, reconnect states, and result screens.",
            points: ["Lobby prototype", "Room states", "Result flow"]
        },
        {
            image: "assets/img/GameDevelopment/rummy-game-5.png",
            title: "Economy & LiveOps Prototypes",
            description: "Prototype wallet, store, offers, daily rewards, missions, battle pass, seasonal events, reward claims, and monetization paths.",
            points: ["Wallet flow", "Store journey", "Event screens"]
        }
    ];

    const prototypeTypes = [
        { image: "assets/img/GameDevelopment/Best-Ludo-Game.png", title: "Board Game Prototype", description: "Validate dice flow, player turns, room states, invite flow, betting rules, and results before development." },
        { image: "assets/img/GameDevelopment/card-game1.png", title: "Card Game Prototype", description: "Test table layout, card actions, timers, pot states, wallet checks, round results, and error states." },
        { image: "assets/img/GameDevelopment/fantasy-cricket-app-img.png", title: "Fantasy App Prototype", description: "Validate contest selection, team builder, wallet entry, leaderboard, live scoring, and result flows." },
        { image: "assets/img/GameDevelopment/quizappfeat.png", title: "Quiz Game Prototype", description: "Test answer selection, timers, feedback states, lifelines, scoring, streaks, and progression screens." },
        { image: "assets/img/GameDevelopment/endless-runner-game-thumbnail.png", title: "Casual Game Prototype", description: "Validate menus, upgrade flow, missions, powerups, rewards, and quick replay loops." },
        { image: "assets/img/GameDevelopment/services-vr-game-hero-1.jpg", title: "AR/VR Interaction Prototype", description: "Map spatial interactions, controller prompts, comfort flows, overlays, and world-space UI behavior." }
    ];

    const deliverables = [
        "Clickable Prototype",
        "Interaction Notes",
        "Screen States",
        "User Flow Map",
        "Gameplay Flow",
        "Lobby Prototype",
        "Wallet Prototype",
        "Store Prototype",
        "Reward Prototype",
        "LiveOps Prototype",
        "UX Feedback Notes",
        "Developer Handoff"
    ];

    const gallery = [
        { image: "assets/img/GameDevelopment/Battle-Royale-Game-th.png", title: "Combat Flow Prototype" },
        { image: "assets/img/GameDevelopment/Arcade_Games_Development.png", title: "Arcade Loop Prototype" },
        { image: "assets/img/GameDevelopment/Board-Game-Banner.png", title: "Board Game Prototype" },
        { image: "assets/img/GameDevelopment/onlinr-casino.jpg", title: "Casino Flow Prototype" },
        { image: "assets/img/GameDevelopment/fantasy-football-app-img.png", title: "Fantasy Sports Prototype" },
        { image: "assets/img/GameDevelopment/ludowings-img.jpg", title: "Casual Board Prototype" },
        { image: "assets/img/GameDevelopment/SCREEN4.png", title: "Gameplay State Prototype" },
        { image: "assets/img/GameDevelopment/quiztopmobile.png", title: "Mobile UI Prototype" }
    ];

    const process = [
        { step: "01", title: "Prototype Scope", description: "We define the game flows, screens, user decisions, monetization paths, test goals, and prototype fidelity." },
        { step: "02", title: "Flow Mapping", description: "We map screen connections, player actions, game states, edge cases, backend events, and decision points." },
        { step: "03", title: "Interactive Build", description: "We create clickable flows for HUDs, menus, lobbies, stores, wallets, rewards, onboarding, and LiveOps screens." },
        { step: "04", title: "Review & Iterate", description: "We test the prototype with stakeholders, review friction points, adjust screen logic, and refine interaction behavior." },
        { step: "05", title: "Handoff", description: "We deliver prototype links, screen states, UX notes, flow maps, assets, and implementation guidance for design and development." }
    ];

    const benefits = [
        { icon: "fa-solid fa-hand-pointer", title: "Test Before Building", description: "Clickable prototypes expose weak flows, missing states, and confusing interactions before production effort begins." },
        { icon: "fa-solid fa-gamepad", title: "Gameplay-Aware Validation", description: "We prototype around player motivation, controls, feedback, rewards, competition, and session flow." },
        { icon: "fa-solid fa-coins", title: "Economy Flow Clarity", description: "Store, wallet, offer, reward, purchase, and mission flows become easier to review before implementation." },
        { icon: "fa-solid fa-users", title: "Stakeholder Alignment", description: "A clickable prototype helps founders, designers, developers, and clients evaluate the same product direction." }
    ];

    const faqs = [
        {
            question: "What is game prototyping for UI/UX?",
            answer: "Game prototyping connects important screens into clickable flows so teams can test gameplay UX, menus, lobbies, wallets, rewards, stores, and LiveOps journeys before development."
        },
        {
            question: "Can you prototype multiplayer game flows?",
            answer: "Yes. We prototype matchmaking, room creation, private tables, ready states, player turns, reconnect states, results, leaderboards, and post-game actions."
        },
        {
            question: "Can prototypes include wallet and store flows?",
            answer: "Yes. We can prototype coins, gems, wallet screens, purchase paths, offers, rewards, transaction states, error states, and monetization journeys."
        },
        {
            question: "Do you provide developer handoff after prototyping?",
            answer: "Yes. We provide prototype links, screen states, flow maps, interaction notes, UX decisions, and implementation guidance."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Game UI UX Prototyping Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Game Prototyping",
        "areaServed": "Worldwide",
        "description": "Clickable game UI and UX prototyping services for gameplay flows, lobbies, wallets, stores, rewards, LiveOps, multiplayer states, and developer handoff.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Game UI/UX Prototyping Services | Clickable Game Prototypes | Oengines</title>
                <meta name="description" content="Oengines Games LLP creates clickable game UI/UX prototypes for gameplay flows, lobbies, wallets, stores, rewards, LiveOps, multiplayer states, and developer handoff." />
                <meta name="keywords" content="game prototyping, game UI prototype, game UX prototype, clickable game prototype, mobile game prototype, gaming app prototype, UX prototyping services" />
                <link rel="canonical" href="https://www.oengines.com/game-prototyping" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="prototype-page">
                <section className="prototype-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="prototype-eyebrow">Game UI/UX Prototyping</span>
                                <h1>Validate Game Screens, Interactions & Player Flows Before Development</h1>
                                <p>Oengines Games LLP creates clickable prototypes for game UI/UX, including gameplay loops, lobbies, wallets, stores, rewards, LiveOps screens, multiplayer states, and stakeholder review flows.</p>
                                <div className="prototype-actions">
                                    <a href="/contact" className="as-btn">Start Prototype <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="prototype-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="prototype-hero-stats">
                                    <div><strong>Click</strong><span>Interactive Flows</span></div>
                                    <div><strong>Test</strong><span>UX Before Build</span></div>
                                    <div><strong>Ship</strong><span>Clear Handoff</span></div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="prototype-hero-gallery">
                                    <img className="prototype-main-shot" src="assets/img/GameDevelopment/Creative-UI-Character-Design.png" alt="Game UI prototype" />
                                    <div className="prototype-mini-grid">
                                        <img src="assets/img/GameDevelopment/quiztopmobile.png" alt="Mobile game prototype" />
                                        <img src="assets/img/GameDevelopment/multiplayer_game.jpg" alt="Multiplayer prototype" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="prototype-service-section space">
                    <div className="container">
                        <div className="prototype-heading text-center">
                            <span>Prototype Services</span>
                            <h2>Clickable Game Prototypes for UX, Gameplay & Product Validation</h2>
                            <p>Prototypes help your team review decisions, gameplay flow, monetization, and screen behavior before detailed development begins.</p>
                        </div>
                        <div className="row g-4">
                            {services.map((service) => (
                                <div className="col-lg-3 col-md-6" key={service.title}>
                                    <article className="prototype-card">
                                        <img src={service.image} alt={service.title} />
                                        <div>
                                            <h3>{service.title}</h3>
                                            <p>{service.description}</p>
                                            <ul>
                                                {service.points.map((point) => <li key={point}>{point}</li>)}
                                            </ul>
                                        </div>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="prototype-type-section space">
                    <div className="container">
                        <div className="prototype-heading text-center">
                            <span>Prototype Coverage</span>
                            <h2>Game Prototype Flows We Can Build</h2>
                            <p>We create prototypes that make game decisions, interactions, economy, multiplayer, and retention flows easier to evaluate.</p>
                        </div>
                        <div className="prototype-type-grid">
                            {prototypeTypes.map((item) => (
                                <article className="prototype-type-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="prototype-gallery-section space">
                    <div className="container">
                        <div className="prototype-heading text-center">
                            <span>Visual References</span>
                            <h2>Image-Led Prototype Directions for Game Products</h2>
                        </div>
                        <div className="prototype-gallery-grid">
                            {gallery.map((item) => (
                                <figure className="prototype-gallery-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <figcaption>{item.title}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="prototype-assets-section space">
                    <div className="container">
                        <div className="prototype-assets-panel">
                            <div className="prototype-heading mb-0">
                                <span>Deliverables</span>
                                <h2>Prototype Outputs for Product, Design & Development Teams</h2>
                                <p>We deliver prototypes and supporting notes so teams can align on product behavior before production starts.</p>
                            </div>
                            <div className="prototype-assets-list">
                                {deliverables.map((asset) => <span key={asset}>{asset}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="prototype-process-section space">
                    <div className="container">
                        <div className="prototype-heading text-center">
                            <span>Prototype Process</span>
                            <h2>From Screen Flow to Clickable Game Prototype</h2>
                        </div>
                        <div className="prototype-process-grid">
                            {process.map((item) => (
                                <article className="prototype-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="prototype-benefit-section space">
                    <div className="container">
                        <div className="prototype-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Game Prototypes Built Around UX, Economy & Retention</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="prototype-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="prototype-faq-section space">
                    <div className="container">
                        <div className="prototype-heading text-center">
                            <span>FAQ</span>
                            <h2>Game Prototyping Questions</h2>
                        </div>
                        <div className="prototype-faq-grid">
                            {faqs.map((item) => (
                                <article className="prototype-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="prototype-cta">
                    <div className="container">
                        <div className="prototype-cta-box">
                            <div>
                                <span>Validate Before Build</span>
                                <h2>Prototype your game UX with Oengines.</h2>
                                <p>Share your game concept, key screens, player actions, economy flow, and target platform. We will help turn it into a clickable prototype.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Prototype Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
